// A table's style (SB, Features 4), as PowerPoint's Table Design draws its styles, from the deck's own colours so a
// theme change carries it: the style's colour is the box's fill (the accent unless set), the slide's background and
// words make the rest. Each cell's fill, ink, weight and four sides come from here, with the cell's own fill, ink and
// borders over them; the editor, every thumbnail and the .pptx all ask the same function.
//   Classic: S7's look (a header on the colour, soft rules between rows).
//   Light: no header fill, a rule in the colour under it, hairlines between rows.
//   Medium: as PowerPoint's Medium Style 2, the header on the colour, rows on its tints, light lines between cells.
//   Grid: every cell ruled, the header on the slide's surface.
//   Dark: the header in the words' colour, rows on quiet tints of it.
//   Minimal: no fills, a strong rule under the header and at the foot.

import type { Palette, SlideBox } from "../../../../shared/slideDesign";
import type { SlideTable, TableCell } from "../../../../shared/slideTable";
import { ownEdge, type Edge, type TableStyle } from "../../../../shared/slideTableFormat";
import { mixHex } from "../../../../shared/deckTheme";
import { readableOn } from "../../../themes/themes";
import { paint } from "../Boxes";

export interface Side {
  color: string;
  w: number;
  dash?: "dash" | "dot";
}

export interface CellLook {
  fill: string | undefined;
  ink: string;
  bold: boolean;
  t: Side | null;
  r: Side | null;
  b: Side | null;
  l: Side | null;
}

const hex6 = (c: string) => (/^#[0-9a-f]{6}$/i.test(c) ? c : "#888888");
const readable = (fill: string) => (readableOn(hex6(fill)) === "#ffffff" ? "#FFFFFF" : "#111317");

/** Whether a table is drawn cell by cell from its style (a style, a first column, or cells with their own borders). */
export const styled = (t: SlideTable) => !!t.style || !!t.first || t.rows.some((r) => r.cells.some((c) => !!c.bd));

/** One cell's look on this slide: its style's, then its own. */
export function cellLook(b: SlideBox, r: number, c: number, palette: Palette): CellLook {
  const t = b.table!;
  const cell: TableCell = t.rows[r]?.cells[c] ?? {};
  const style: TableStyle = t.style ?? "classic";
  const S = hex6(paint(b.fill ?? "accent", palette, palette.accent));
  const bg = hex6(palette.background);
  const tx = hex6(palette.text);
  const text = paint(b.color, palette, palette.text);
  const head = !!t.head && r === 0;
  const bodyRow = t.head ? r - 1 : r;
  const banded = !!t.band && !head && bodyRow % 2 === 1;
  const first = !!t.first && c === 0 && !head;
  const rs = cell.rs ?? 1;
  const lastRow = r + rs - 1 === t.rows.length - 1;
  const lw = b.lineW ?? 1.5;
  const soft = (k: number, w = 1.5): Side => ({ color: mixHex(bg, tx, k), w });
  let fill: string | undefined;
  let ink = text;
  let bold = head || first;
  const e: Record<"t" | "r" | "b" | "l", Side | null> = { t: null, r: null, b: null, l: null };
  switch (style) {
    case "classic":
      if (head) fill = S;
      else if (banded) fill = mixHex(bg, S, 0.1);
      if (first) fill = fill ?? mixHex(bg, S, 0.1);
      if (r > 0 && !(t.head && r === 1)) e.t = soft(0.18, lw);
      if (lastRow) e.b = soft(0.18, lw);
      break;
    case "light":
      if (banded) fill = mixHex(bg, S, 0.07);
      if (head) e.b = { color: S, w: 2.5 };
      else if (r > 0 && !(t.head && r === 1)) e.t = soft(0.14, 1);
      if (lastRow) e.b = soft(0.26, 1.5);
      if (first) ink = S;
      break;
    case "medium":
      fill = head ? S : first ? mixHex(bg, S, 0.3) : banded ? mixHex(bg, S, 0.24) : mixHex(bg, S, 0.12);
      if (r > 0) e.t = { color: bg, w: 2 };
      if (c > 0) e.l = { color: bg, w: 2 };
      break;
    case "grid":
      if (head) fill = hex6(palette.surface);
      else if (banded) fill = mixHex(bg, tx, 0.045);
      e.t = e.b = e.l = e.r = soft(0.3, lw);
      break;
    case "dark":
      fill = head ? tx : first ? mixHex(bg, S, 0.16) : banded ? mixHex(bg, tx, 0.1) : mixHex(bg, tx, 0.04);
      if (r > 0 && !(t.head && r === 1)) e.t = soft(0.16, 1);
      break;
    case "minimal":
      if (banded) fill = mixHex(bg, tx, 0.045);
      if (head) {
        ink = hex6(palette.muted);
        e.b = { color: tx, w: 2.5 };
      }
      if (lastRow) e.b = { color: tx, w: 2.5 };
      break;
  }
  // the box's lines, as S7's Borders set them: none at all, or a grid in a colour, over the style's
  if (b.line === null) e.t = e.r = e.b = e.l = null;
  else if (b.line) e.t = e.r = e.b = e.l = { color: paint(b.line, palette, palette.text), w: lw };
  // the ink on a fill the style or the cell gives, unless the box or the cell says its own
  if (fill && (head || style === "medium" || style === "dark" || first) && !b.color) ink = head && style === "light" ? text : readable(fill);
  if (head && style === "minimal") ink = hex6(palette.muted);
  if (cell.fill === null) fill = undefined;
  else if (cell.fill) {
    fill = paint(cell.fill, palette, palette.surface);
    if (!cell.color) ink = readable(fill);
  }
  if (cell.color) ink = paint(cell.color, palette, ink);
  // the cell's own sides, and those its neighbours give the sides it shares
  for (const k of ["t", "r", "b", "l"] as const) {
    const own = ownEdge(t, r, c, k);
    if (own !== undefined) e[k] = own ? sideOf(own, palette) : null;
  }
  return { fill, ink, bold, ...e };
}

const sideOf = (x: Edge, palette: Palette): Side => ({ color: paint(x.color, palette, palette.text), w: x.w, ...(x.dash ? { dash: x.dash } : {}) });

/** A side as CSS. */
export const cssSide = (s: Side | null) => (s ? `${s.w}px ${s.dash === "dash" ? "dashed" : s.dash === "dot" ? "dotted" : "solid"} ${s.color}` : "none");
